import { TextButton, TextLink } from '@oneaudi/unified-web-components';
import type { FC } from 'react';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import type { CarlineAvailableStock } from '../../graphql/types';
import type { CompareCarline } from '../context/CompareContext';
import { useContent } from '../context/ContentContext';
import { useFeatureAppEnv } from '../context/FeatureAppEnvContext';
import withTracking from '../hoc/withTracking';
import type { TrackingManager } from '../hooks/useTrackingManager';
import { useI18n } from '../i18n';
import { CompareCheckbox } from './compare/CompareCheckbox';

interface ActionsProps {
  readonly title: string;
  readonly group: string;
  readonly discoverLink?: string;
  readonly configureLink?: string;
  readonly availableStock?: CarlineAvailableStock;
  readonly trackingManager: TrackingManager;
  readonly carline: CompareCarline;
}

const Actions: FC<ActionsProps> = ({
  title,
  group,
  discoverLink,
  configureLink,
  availableStock,
  trackingManager,
  carline,
}: ActionsProps) => {
  const { 'audi-number-formatter-service': numberFormatterService } =
    useFeatureAppEnv().featureServices;

  const availableNewCarsText =
    availableStock?.newCars &&
    useI18n('availableNewCars', {
      value: numberFormatterService.formatNumber(availableStock.newCars.amount),
    });

  const availableUsedCarsText =
    availableStock?.usedCars &&
    useI18n('availableUsedCars', {
      value: numberFormatterService.formatNumber(availableStock.usedCars.amount),
    });

  const {
    ctaDisplayOptions: {
      showConfigureCTA,
      showNewAndUsedCarsCTA,
      showNewCarsCTA,
      showUsedCarsCTA,
      discoverLinkIsPrimaryCta,
    },
    compareMode,
  } = useContent();

  // Grace contract: fall back to legacy toggle until all markets migrate.
  const newCarsEnabled = Boolean(showNewAndUsedCarsCTA || showNewCarsCTA);
  const usedCarsEnabled = Boolean(showNewAndUsedCarsCTA || showUsedCarsCTA);

  const discoverCTALabel = useI18n('discoverCTALabel');
  const configureCTALabel = useI18n('configureCTALabel');
  const theme = useTheme();

  const handleCtaTrackingButtonClick = (link: string, label: string, linkValue: string) => {
    trackingManager.click(link, label, linkValue, group, title, undefined, 'button');
  };

  const renderVtpInfo = (
    type: 'new' | 'used',
    amount: number | undefined,
    url: string | undefined,
    label: string | undefined,
    testId: string,
  ) => {
    const isEmpty = amount === 0;

    return (
      <TextLink
        data-testid={testId}
        {...(!isEmpty && url
          ? {
              href: url,
              rel: 'noopener',
              target: '_self',
            }
          : {
              type: 'button',
              disabled: true,
            })}
        margin={[theme.spacing.fixed150, 0, theme.spacing.fixed150, 0]}
        size="sm"
        variant="standalone"
        onClick={() => handleCtaTrackingButtonClick(url || '', label || '', `${type} cars`)}
      >
        {label}
      </TextLink>
    );
  };

  const createCardAction = () => (
    <ActionWrapper>
      <Holder>
        {compareMode !== 'off' && configureLink !== undefined && (
          <CheckboxWrapper>
            <CompareCheckbox carline={carline} />
          </CheckboxWrapper>
        )}
        {compareMode !== 'compare-only' &&
          (discoverLinkIsPrimaryCta ? (
            <>
              {discoverLink && (
                <TextButton
                  href={discoverLink}
                  variant="primary"
                  size="sm"
                  isFullWidth
                  onClick={() =>
                    handleCtaTrackingButtonClick(discoverLink, discoverCTALabel, 'discover')
                  }
                  aria-label={discoverCTALabel}
                  data-testid="primaryCta"
                  margin={[0, 0, configureLink ? theme.spacing.fixed150 : theme.spacing.fixed50, 0]}
                >
                  {discoverCTALabel}
                </TextButton>
              )}
              {!!configureLink && showConfigureCTA && (
                <TextButton
                  href={configureLink}
                  variant="secondary"
                  size="sm"
                  isFullWidth
                  onClick={() =>
                    handleCtaTrackingButtonClick(configureLink, configureCTALabel, 'configure')
                  }
                  aria-label={configureCTALabel}
                  data-testid="secondaryCta"
                  margin={[0, 0, theme.spacing.fixed50, 0]}
                >
                  {configureCTALabel}
                </TextButton>
              )}
            </>
          ) : (
            <>
              {!!configureLink && showConfigureCTA && (
                <TextButton
                  href={configureLink}
                  variant="primary"
                  size="sm"
                  isFullWidth
                  data-testid="primaryCta"
                  onClick={() =>
                    handleCtaTrackingButtonClick(configureLink, configureCTALabel, 'configure')
                  }
                  margin={[0, 0, discoverLink ? theme.spacing.fixed150 : theme.spacing.fixed50, 0]}
                >
                  {configureCTALabel}
                </TextButton>
              )}
              {discoverLink && (
                <TextButton
                  href={discoverLink}
                  variant={showConfigureCTA ? 'secondary' : 'primary'}
                  size="sm"
                  isFullWidth
                  onClick={() =>
                    handleCtaTrackingButtonClick(discoverLink, discoverCTALabel, 'discover')
                  }
                  aria-label={discoverCTALabel}
                  data-testid={showConfigureCTA ? 'secondaryCta' : 'primaryCta'}
                  margin={[0, 0, theme.spacing.fixed50, 0]}
                >
                  {discoverCTALabel}
                </TextButton>
              )}
            </>
          ))}
        {compareMode !== 'compare-only' && (newCarsEnabled || usedCarsEnabled) && (
          <>
            {newCarsEnabled
              ? renderVtpInfo(
                  'new',
                  availableStock?.newCars?.amount,
                  availableStock?.newCars?.url,
                  availableNewCarsText ?? undefined,
                  'newCarsVtpLink',
                )
              : null}

            {usedCarsEnabled
              ? renderVtpInfo(
                  'used',
                  availableStock?.usedCars?.amount,
                  availableStock?.usedCars?.url,
                  availableUsedCarsText ?? undefined,
                  'usedCarsVtpLink',
                )
              : null}
          </>
        )}
      </Holder>
    </ActionWrapper>
  );

  return <>{createCardAction()}</>;
};

export default withTracking(Actions);

const ActionWrapper = styled.div`
  /* position: relative; */
`;

const Holder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 10;
  background-color: var(--backgroundCanvas);
`;

const CheckboxWrapper = styled.div`
  align-self: start;
`;
